import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import type { BillingSummary } from '@/lib/billing';
import { ACCESS } from '@/features/access/access';
import { useRoleAccess } from '@/features/access/use-role-access';
import {
  scheduledChangeLine,
  scheduledChangeUndoLabel,
} from './lifecycle-helpers';

// Unexported so this module still exports only React components (Vite Fast
// Refresh). The rail's warning-line recipe, typed once for both branches below.
const RAIL_PENDING_LINE: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: 'var(--warn-600)',
};

type Props = {
  scheduledChange: BillingSummary['scheduledChange'];
  scheduledResumeAt: BillingSummary['scheduledResumeAt'];
};

/**
 * The sidebar plan card's "what's coming" line — a pending cancel/pause/resume,
 * or the return date of a pause that has already landed.
 *
 * Split out of `PlanCard.tsx` because that file is at the 250-line ceiling (the
 * same reason `PlanCardUsage` exists), not because it is reused elsewhere.
 *
 * The status chip above it keeps showing the TRUE current status — the plan is
 * still fully active until the effective date — so this gets its own line rather
 * than relabelling the chip. The rail is ~200px wide so the line truncates,
 * hence the `title`.
 *
 * **Owner-gated CTA.** This card rides `GET /billing/summary`, which any org
 * member can read, but the lifecycle actions behind the CTA are OWNER-only and
 * `/billing` itself renders nothing but an info banner for everyone else. So a
 * member gets exactly what they got before — plain text — and only an owner gets
 * the clickable line. Sending a member to `/billing?dialog=…` would be a CTA
 * whose only destination is a page telling them they can't do this.
 *
 * The dialog lives on `/billing` (one instance, owned by
 * `SubscriptionDetailsCard`), so the CTA has to navigate AND name the dialog to
 * open on arrival — hence the `?dialog=` param rather than an in-rail dialog.
 */
export function PlanCardPendingChange({ scheduledChange, scheduledResumeAt }: Props) {
  const navigate = useNavigate();
  const { allowed: canManageBilling } = useRoleAccess(ACCESS.billingManage);

  const line = scheduledChangeLine(scheduledChange, scheduledResumeAt);
  const undoLabel = scheduledChangeUndoLabel(scheduledChange);
  if (!line) return null;

  // A pending RESUME yields no label: it is a good outcome with nothing to undo.
  const canUndo = canManageBilling && undoLabel != null;

  // One set of presentation props for both branches — the element type is the
  // only thing that differs, so duplicating the style/title/truncation into two
  // near-identical returns would be two copies to keep in sync.
  const shared = {
    className: cn('truncate', canUndo && 'cursor-pointer text-left underline-offset-2 hover:underline'),
    style: RAIL_PENDING_LINE,
    title: line,
  };

  return canUndo ? (
    <button
      type="button"
      {...shared}
      // The visible text says what's coming; the accessible name has to also say
      // what activating it does, since "Pauses on Aug 30" alone reads as a label,
      // not a control. Visible text stays a prefix of the name (WCAG 2.5.3).
      aria-label={`${line}. ${undoLabel}`}
      onClick={() => void navigate({ to: '/billing', search: { dialog: 'keep-subscription' } })}
    >
      {line}
    </button>
  ) : (
    <div {...shared}>{line}</div>
  );
}
